import type { QuoteTemplateEntry } from '@common/types/quoteTemplates';
import {
    Alert,
    AlertTitle,
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    type SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useTemplates } from '@/queries/templates';
import useQuoteIntakeSetupStore from '@/stores/useQuoteIntakeSetupStore';
import { theme } from '@/theme';

type IntakeOnlyTemplateOption = Pick<QuoteTemplateEntry, 'quoteTemplateName' | 'quoteTemplateId'>;

export default function PreIntake() {
    const { activeLawFirmId } = useLawFirmContext();
    const [formData, setFormData, activeQuote] = useQuoteIntakeSetupStore((state) => [
        state.formData,
        state.setFormData,
        state.activeQuote,
    ]);
    const lawFirmId = activeQuote.lawFirmId ?? activeLawFirmId;
    const [showTip, setShowTip] = useState(true);
    const isIntakeCreated = formData.step0.isIntakeCreated;

    const { data: templates = [] } = useTemplates(lawFirmId);

    const quoteTemplateNames = useMemo<IntakeOnlyTemplateOption[]>(() => {
        return templates
            .filter(
                (item) =>
                    item.templateType === 'IntakeOnly' &&
                    item.process === formData.step0.transactionType,
            )
            .map((item) => ({
                quoteTemplateName: item.quoteTemplateName,
                quoteTemplateId: item.quoteTemplateId,
            }));
    }, [templates, formData.step0.transactionType]);

    const handleChange = (event: SelectChangeEvent<number | ''>) => {
        const value = event.target.value;
        setFormData('step0', {
            ...formData.step0,
            referencedIntakeOnlyQuoteTemplateId: value === '' ? null : Number(value),
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                boxSizing: 'border-box',
                maxHeight: 'calc(100vh - 168px)', // Todo: Move the heights of headers and footers to some sort of variables to better manage this
                minHeight: '400px',
                width: 'calc(100% - 64px)',
                padding: '32px',
                margin: '32px',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.neutral.borderColor}`,
                backgroundColor: 'neutral.white',
                overflowY: 'auto',
            }}
        >
            {showTip && !isIntakeCreated && (
                <Alert
                    icon={<InfoSolid />}
                    severity="info"
                    onClose={() => {
                        setShowTip(false);
                    }}
                >
                    <AlertTitle>Disclaimer</AlertTitle>
                    <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                        Changes to the selection are not allowed. Make sure you're choosing the
                        correct option before clicking 'Next.'
                    </Typography>
                </Alert>
            )}

            <Typography variant="h1">
                Are you creating your intake form from scratch or based on an existing template?
            </Typography>
            <FormControl disabled={isIntakeCreated}>
                <RadioGroup
                    onChange={(event) => {
                        setFormData('step0', {
                            ...formData.step0,
                            intakeFormAdded: event.target.value,
                        });
                    }}
                    value={formData.step0.intakeFormAdded}
                >
                    <FormControlLabel value="Manually" control={<Radio />} label="Manually" />
                    <FormControlLabel
                        value="Reference"
                        control={<Radio />}
                        label="Create by reference of intake only"
                    />
                    <FormControlLabel
                        value="Copy"
                        control={<Radio />}
                        label="Copy from existing intake only"
                    />
                </RadioGroup>
            </FormControl>

            {(formData.step0.intakeFormAdded === 'Reference' ||
                formData.step0.intakeFormAdded === 'Copy') && (
                <FormControl fullWidth disabled={isIntakeCreated}>
                    <InputLabel>List of intake only templates</InputLabel>
                    <Select
                        label="List of intake only templates"
                        value={formData.step0.referencedIntakeOnlyQuoteTemplateId ?? ''}
                        onChange={handleChange}
                    >
                        {quoteTemplateNames.map((template) => (
                            <MenuItem
                                key={template.quoteTemplateId}
                                value={template.quoteTemplateId}
                            >
                                {template.quoteTemplateName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
}
